import React, {useEffect, useMemo, useState} from "react";
import {useDispatch, useSelector} from "react-redux";
import {Checkbox} from "primereact/checkbox";
import {InputText} from "primereact/inputtext";
import {InputTextarea} from "primereact/inputtextarea";
import Avatar from "../../../system/components/avatar";
import Icon from "../../../system/components/icon";
import {setMessage} from "../../../system/app-slice";
import SelectMembers from "../../tasks/components/select-members";
import {goalUpdateApi} from "../../../models/goal-update/goal-update-api";
import {UpdateCardView} from "../../inbox/update-chat-card";

// «ارسال به‌صورت پیام» at the bottom of the goal and project update modals (boss's task «تغییر رفتار مودال نمایش
// بروزرسانی پروژه و هدف»; docs/tasks/23-update-share-to-chat.md): the update goes as a card message
//   - as a private message to anyone in the workspace
//   - into group chats I am a member of
// The card in the preview is the one the receivers see in the chat; clicking it there opens this modal.
// `onSend({people, chats, note})` returns the server's answer ({sent, groups, update}).

const GROUP_ICONS = {project: "folder_open", team: "groups", group: "forum"};

export default function UpdateShareToChat({card, recipients = [], onSend, onSent}) {
    const dispatch = useDispatch();
    const membershipData = useSelector((state) => state.app.workspaceMembershipData);
    const memberships = membershipData?.workspace?.workspace_memberships;
    const meId = membershipData?.id;
    const people = useMemo(() => (memberships || []).filter((membership) => membership?.id !== meId && !["removed", "restricted"].includes(membership?.memberShipStatus)), [memberships, meId]);
    const [open, setOpen] = useState(false);
    const [selectedPeople, setSelectedPeople] = useState([]);
    const [groups, setGroups] = useState(null);
    const [groupsFailed, setGroupsFailed] = useState(false);
    const [selectedGroups, setSelectedGroups] = useState([]);
    const [groupFilter, setGroupFilter] = useState("");
    const [note, setNote] = useState("");
    const [sending, setSending] = useState(false);

    useEffect(() => {
        if (!open || groups) return;
        let cancelled = false;
        goalUpdateApi.shareChats()
            .then((list) => { if (!cancelled) setGroups(Array.isArray(list) ? list : []); })
            .catch(() => { if (!cancelled) setGroupsFailed(true); });
        return () => { cancelled = true; };
    }, [open, groups]);

    const visibleGroups = (groups || []).filter((group) => !groupFilter.trim() || String(group.title).includes(groupFilter.trim()));
    const count = selectedPeople.length + selectedGroups.length;
    const label = [
        selectedPeople.length ? `${selectedPeople.length.toLocaleString("fa-IR")} نفر` : null,
        selectedGroups.length ? `${selectedGroups.length.toLocaleString("fa-IR")} گروه` : null,
    ].filter(Boolean).join(" و ");

    const send = async () => {
        if (!count || sending) return;
        setSending(true);
        try {
            const result = await onSend({
                people: selectedPeople.map((membership) => membership.documentId || membership.id),
                chats: selectedGroups,
                note: note.trim() || null,
            });
            setSelectedPeople([]);
            setSelectedGroups([]);
            setNote("");
            setOpen(false);
            dispatch(setMessage({severity: "success", content: `بروزرسانی به‌صورت پیام برای ${label} ارسال شد.`}));
            onSent?.(result);
        } catch (error) {
            dispatch(setMessage({severity: "error", content: "ارسال انجام نشد. دوباره تلاش کنید."}));
        } finally {
            setSending(false);
        }
    };

    return (
        <section className="gu-card p-4 gu-no-print">
            <div className="flex flex-wrap align-items-center justify-content-between gap-2 mb-3">
                <h3 className="m-0 flex align-items-center gap-2 text-base font-bold"><Icon icon="mi mi:forum" className="text-xl gu-muted"/>ارسال به‌صورت پیام</h3>
                <button type="button" className="gu-soft-button gu-soft-button--solid" onClick={() => setOpen((value) => !value)}>
                    <Icon icon={`mi mi:${open ? "close" : "send"}`} className="text-base"/>{open ? "انصراف" : "ارسال در گفت‌وگو"}
                </button>
            </div>

            {open && (
                <div className="flex flex-column gap-3 mb-3 p-3 border-round-xl" style={{background: "#fcf9f8"}}>
                    <div className="grid m-0">
                        <div className="col-12 md:col-6 p-1 flex flex-column gap-2">
                            <span className="text-xs font-bold text-700 flex align-items-center gap-1"><Icon icon="mi mi:person" className="text-base"/>پیام شخصی به افراد سازمان</span>
                            <SelectMembers members={people} selectedMembers={selectedPeople} onChange={(event) => setSelectedPeople(event.members || [])}/>
                            <span className="text-2xs gu-muted">برای هر نفر در گفت‌وگوی خصوصی شما با او ارسال می‌شود.</span>
                        </div>
                        <div className="col-12 md:col-6 p-1 flex flex-column gap-2">
                            <span className="text-xs font-bold text-700 flex align-items-center gap-1"><Icon icon="mi mi:groups" className="text-base"/>گروه‌هایی که عضو آن هستید</span>
                            {groups === null && !groupsFailed && <span className="text-xs gu-muted"><i className="pi pi-spin pi-spinner text-xs ml-1"/>در حال بارگذاری…</span>}
                            {groupsFailed && <span className="text-xs" style={{color: "#b91c1c"}}>فهرست گروه‌ها بارگذاری نشد.</span>}
                            {groups && !groups.length && <span className="text-xs gu-muted">عضو هیچ گروهی نیستید.</span>}
                            {groups && groups.length > 6 && (
                                <InputText value={groupFilter} onChange={(event) => setGroupFilter(event.target.value)} placeholder="جستجوی گروه" className="text-xs p-2"/>
                            )}
                            {groups && groups.length > 0 && (
                                <div className="flex flex-column gap-1 overflow-auto" style={{maxHeight: "11rem"}}>
                                    {visibleGroups.map((group) => (
                                        <label key={group.documentId} className="flex align-items-center gap-2 p-2 border-round-lg bg-white cursor-pointer text-xs">
                                            <Checkbox checked={selectedGroups.includes(group.documentId)} onChange={(event) => setSelectedGroups((current) => (
                                                event.checked ? [...current, group.documentId] : current.filter((id) => id !== group.documentId)
                                            ))}/>
                                            <Icon icon={`mi mi:${GROUP_ICONS[group.type] || "forum"}`} className="text-base gu-muted"/>
                                            <span className="flex-1">{group.title}</span>
                                            <span className="text-2xs gu-muted">{`${Number(group.members || 0).toLocaleString("fa-IR")} عضو`}</span>
                                        </label>
                                    ))}
                                </div>
                            )}
                        </div>
                    </div>

                    <label className="flex flex-column gap-1 text-xs font-bold text-700">یادداشت همراه پیام (اختیاری)
                        <InputTextarea rows={2} autoResize value={note} onChange={(event) => setNote(event.target.value)} placeholder="مثلاً: لطفاً تا فردا نظرتان را بگویید" className="text-xs"/>
                    </label>

                    <div className="flex flex-column gap-2">
                        <span className="text-xs font-bold text-700">پیش‌نمایش پیام</span>
                        {note.trim() && <span className="text-xs text-700 white-space-pre-wrap">{note.trim()}</span>}
                        <UpdateCardView reference={card} preview/>
                    </div>

                    <div className="flex justify-content-end">
                        <button type="button" className="gu-soft-button gu-soft-button--primary" disabled={!count || sending} onClick={send}>
                            {sending ? "در حال ارسال…" : count ? `ارسال برای ${label}` : "ارسال"}
                        </button>
                    </div>
                </div>
            )}

            <span className="block text-xs gu-muted mb-2">ارسال‌شده به‌صورت پیام شخصی برای:</span>
            {recipients.length ? (
                <div className="flex flex-wrap gap-2">
                    {recipients.map((person) => (
                        <span key={person.documentId || person.id} className="gu-person">
                            <Avatar memberData={person.member} style={{width: "2rem", height: "2rem"}} className="text-3xs"/>
                            {person.member?.fullname}
                        </span>
                    ))}
                </div>
            ) : <span className="text-sm gu-muted">هنوز برای کسی ارسال نشده است.</span>}
        </section>
    );
}
